import { TypedDocumentNode, gql } from '@apollo/client';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { range } from '@sorare/core/src/lib/arrays';

import { getLeagueTrackSlotsInfo } from '../utils';
import { LeagueTrckSlot } from './LeagueTrckSlot';
import type { LeagueTrckSlots_so5LeagueTrack } from './__generated__/index.graphql';

const List = styled(Horizontal).attrs({ center: true })`
  overflow: auto;
  padding: 0 var(--unit);
  max-width: 100%;
`;

type Props = {
  so5LeagueTrack: LeagueTrckSlots_so5LeagueTrack;
  selectedIndex: number;
  setSelectedIndex: (index: number) => void;
};

export const LeagueTrckSlots = ({
  so5LeagueTrack,
  selectedIndex,
  setSelectedIndex,
}: Props) => {
  const { nbSlots, nbContenders: firstEmptySlot } =
    getLeagueTrackSlotsInfo(so5LeagueTrack);

  const { entrySo5Leaderboard, mySo5LeaderboardContenders, completed } =
    so5LeagueTrack;

  return (
    <List gap={3}>
      {range(nbSlots).map(i => (
        <LeagueTrckSlot
          key={i}
          index={i}
          selected={i === selectedIndex}
          onClick={() => setSelectedIndex(i)}
          canCompose={entrySo5Leaderboard.canCompose.value}
          firstEmptySlot={firstEmptySlot === i}
          so5LeaderboardContender={mySo5LeaderboardContenders.at(i)}
          rarity={entrySo5Leaderboard.mainRarityType || Rarity.common}
          completed={!!completed}
        />
      ))}
    </List>
  );
};

LeagueTrckSlots.fragments = {
  so5LeagueTrack: gql`
    fragment LeagueTrckSlots_so5LeagueTrack on So5LeagueTrack {
      slug
      completed
      entrySo5Leaderboard {
        slug
        mainRarityType
        canCompose {
          value
        }
      }
      mySo5LeaderboardContenders(includeHiddenManagerTeam: true) {
        slug
        ...LeagueTrckSlot_so5LeaderboardContender
      }
      ...getLeagueTrackSlotsInfo_so5LeagueTrack
    }
    ${LeagueTrckSlot.fragments.so5LeaderboardContender}
    ${getLeagueTrackSlotsInfo.fragments.so5LeagueTrack}
  ` as TypedDocumentNode<LeagueTrckSlots_so5LeagueTrack>,
};
